import { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useBlocker } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Container,
  Divider,
  Group,
  Image,
  NumberInput,
  Popover,
  Paper,
  Radio,
  Select,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  Title,
  Loader,
  Center,
  Modal,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { SampleImageUploader } from "../../../../components/SampleImageUploader";
import AccessTierControl from "../../../../components/AccessTierControl";
import {
  IconArrowLeft,
  IconBolt,
  IconDeviceFloppy,
  IconInfoCircleFilled,
  IconRocket,
} from "@tabler/icons-react";
import { Endpoints } from "../../../../lib/endpoints";
import {
  PerformRequest,
  UploadFiles,
} from "../../../../hooks/usePerformRequest";
import spotifyLogo from "../../../../assets/img/brands/spotify.png";
import youtubeLogo from "../../../../assets/img/brands/youtube.png";
import { HttpCodes } from "../../../../lib/data";
import "../styles.css";
import "./styles.css";

const sanitizeUsername = (input: string): string => {
  if (!input) return "";
  let clean = input.trim();
  if (clean.startsWith("@")) {
    clean = clean.substring(1);
  }
  try {
    if (clean.includes("/") && (clean.startsWith("http://") || clean.startsWith("https://") || clean.includes(".com/"))) {
      let urlString = clean;
      if (!urlString.startsWith("http://") && !urlString.startsWith("https://")) {
        urlString = "https://" + urlString;
      }
      const parsedUrl = new URL(urlString);
      const pathname = parsedUrl.pathname;
      const parts = pathname.split("/").filter(Boolean);
      if (parts.length > 0) {
        let candidate = parts[parts.length - 1];
        if (candidate.startsWith("@")) {
          candidate = candidate.substring(1);
        }
        clean = candidate;
      }
    }
  } catch (e) {
    const parts = clean.split("/").filter(Boolean);
    if (parts.length > 0) {
      let candidate = parts[parts.length - 1];
      if (candidate.startsWith("@")) {
        candidate = candidate.substring(1);
      }
      clean = candidate;
    }
  }
  return clean;
};

type TaskStatus = "draft" | "active" | "completed";

type MissionType =
  | "youtube"
  | "twitter"
  | "spotify"
  | "facebook"
  | "instagram"
  | "whatsapp"
  | "telegram"
  | "tiktok"
  | "visit"
  | "form"
  | "share"
  | "other";

const ACTIONS_BY_TYPE: Record<string, { value: string; label: string }[]> = {
  spotify: [
    { value: "listen", label: "Listen" },
    { value: "save_to_playlist", label: "Save Song to Playlist" },
    { value: "follow_artist", label: "Follow Artist" },
  ],
  youtube: [
    { value: "watch", label: "Watch Video" },
    { value: "subscribe", label: "Subscribe to Channel" },
    { value: "comment", label: "Leave a Comment" },
    { value: "like_video", label: "Like Video" },
  ],
  facebook: [
    { value: "like_post", label: "Like Post" },
    { value: "comment", label: "Comment" },
    { value: "reshare_post", label: "Reshare Post" },
  ],
  twitter: [
    { value: "like_tweet", label: "Like Tweet" },
    { value: "retweet", label: "Retweet" },
    { value: "quote_tweet", label: "Quote Tweet" },
    { value: "follow_account", label: "Follow Account" },
    { value: "comment", label: "Comment" },
    { value: "like_comment", label: "Like Comment" },
    { value: "reply_to_comment", label: "Reply to Comment" },
  ],
  instagram: [
    { value: "like_post_or_reel", label: "Like Reel/Post" },
    { value: "comment", label: "Comment" },
    { value: "follow_account", label: "Follow Account" },
    { value: "like_comment", label: "Like Comment" },
    { value: "reshare_reel", label: "Reshare Reel" },
    { value: "share_to_story", label: "Share to Story" },
    { value: "create_post_or_reel", label: "Create Post/Reel" },
  ],
  whatsapp: [
    { value: "share_to_status", label: "Share to Status" },
    { value: "join_group", label: "Join Group" },
  ],
  telegram: [{ value: "join_channel", label: "Join Channel" }],
  tiktok: [
    { value: "follow_account", label: "Follow Account" },
    { value: "repost_video", label: "Repost Video" },
    { value: "comment", label: "Comment" },
    { value: "like_video", label: "Like Video" },
    { value: "create_post_or_video", label: "Create Post/Video" },
  ],
};

type TaskFormValues = {
  title: string;
  description: string;
  type: MissionType;
  actionType: string;
  campaign: string | null;
  youtubeUrl: string;
  spotifyUrl: string;
  platformUrl: string; // Generic
  requiredListens: number;
  points: number;
  currency: string;
  targetCompletions: number;
  startDate: Date | null;
  expiresAt: Date | null;
  expireAtEndOfDay: boolean;
  verificationInstruction: string;
  status: TaskStatus;
  order: number;
  requiresProofPost: boolean;
  minWatchDurationSeconds: number;
  watchToEnd: boolean;
  cooldownSeconds: number;
  platforms: string[];
  platformRule: "any" | "all";
  minLevel: number;
  isTutorial: boolean;
  squadsOnly: boolean;
  thumbnailUrl: string;
  sampleImages: (string | File)[];
  proofInstructions: string;
  minRequiredLikes: number | null;
  targetUsername: string;
  targetBrandName: string;
  targetPostText: string;
};

type UpdateMissionApiResponse = {
  data?: { id: string };
  message?: string;
  errors?: Record<string, string>;
};

const MissionUpdate = () => {
  const allowNavigateRef = useRef(false);
  const navigate = useNavigate();
  const { taskId } = useParams();

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => {
      if (allowNavigateRef.current) return false;
      return currentLocation.pathname !== nextLocation.pathname;
    }
  );



  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (allowNavigateRef.current) return;
      e.preventDefault();
      e.returnValue = "";
      return "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedTask, setLoadedTask] = useState<any>(null);

  const [success, setSuccess] = useState<string | null>(null);
  const [campaignOptions, setCampaignOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [levels, setLevels] = useState<any[]>([]);

  // Determine minDate for Start Date based on existing value
  const getMinStartDate = () => {
    if (form.values.startDate && form.values.startDate < new Date()) {
      return form.values.startDate;
    }
    return new Date();
  };

  // Determine minDate for Expiry Date based on startDate and existing value
  const getMinExpiresAt = () => {
    const baseDate = form.values.startDate || new Date();
    if (form.values.expiresAt && form.values.expiresAt < baseDate) {
      return form.values.expiresAt;
    }
    return baseDate;
  };

  // Fetch Campaigns
  useEffect(() => {
    const fetchCampaigns = async () => {
      const endpoint = Endpoints.GetCampaigns({ limit: 100, status: "active" }); // Fetch active campaigns
      const r = await PerformRequest<any>({
        method: endpoint.method,
        route: endpoint.route,
      });
      if (r.status === HttpCodes.Success) {
        const options = r.data.data.map((c: any) => ({
          value: c._id,
          label: c.title,
        }));
        setCampaignOptions(options);
      }
    };
    fetchCampaigns();
  }, []);

  // Fetch Levels
  useEffect(() => {
    const fetchLevels = async () => {
      const endpoint = Endpoints.GetLevels();
      const r = await PerformRequest<any>({
        method: endpoint.method,
        route: endpoint.route,
      });
      if (r.status === HttpCodes.Success) {
        setLevels(r.data.data || []);
      }
    };
    fetchLevels();
  }, []);

  const form = useForm<TaskFormValues>({
    initialValues: {
      title: "",
      description: "",
      type: "youtube",
      actionType: "default",
      campaign: null,
      youtubeUrl: "",
      spotifyUrl: "",
      platformUrl: "",
      requiredListens: 1,
      points: 50,
      currency: "COIN",
      targetCompletions: 1000,
      startDate: null,
      expiresAt: null,
      expireAtEndOfDay: false, // Default to false for updates so we can see the date
      verificationInstruction: "",
      status: "draft",
      order: 0,
      requiresProofPost: false,
      minWatchDurationSeconds: 40,
      watchToEnd: false,
      cooldownSeconds: 0,
      platforms: [],
      platformRule: "any",
      minLevel: 1,
      isTutorial: false,
      squadsOnly: false,
      thumbnailUrl: "",
      sampleImages: [],
      proofInstructions: "",
      minRequiredLikes: null,
      targetUsername: "",
      targetBrandName: "",
      targetPostText: "",
    },
    validate: {
      title: (v) => (!v.trim() ? "Title is required" : null),
      youtubeUrl: (v, values) => {
        if (values.type === "youtube") {
          if (!v.trim()) return "Link is required";
        }
        return null;
      },
      spotifyUrl: (v, values) => {
        if (values.type === "spotify" && !v.trim()) {
          return "Link is required";
        }
        return null;
      },
      platformUrl: (v, values) => {
        const needsPlatformUrl = [
          "facebook",
          "twitter",
          "instagram",
          "whatsapp",
          "telegram",
          "tiktok",
        ].includes(values.type);
        if (needsPlatformUrl && !v.trim()) return "Link is required";
        return null;
      },
      sampleImages: (v) =>
        !Array.isArray(v) || v.length < 1
          ? "At least one sample image is required"
          : null,
      expiresAt: (v, values) => {
        if (values.isTutorial) return null; // Tutorials do not expire
        if (!values.expireAtEndOfDay && !v) {
          return "Expiry date is required";
        }
        // Allow past dates if managing existing mission? No, usually expect future updates.
        // But if the mission is expired, we might want to update it to new date.
        // Keeping validation same as create.
        if (v && v.getTime() < Date.now()) {
          //   return "Expiry must be in the future";
          // Commenting this out for update as it might already be expired and we are just viewing
        }
        return null;
      },
      targetPostText: (v, values) => {
        const isYouTubeWatch = values.type === "youtube" && values.actionType === "watch";
        if (!values.isTutorial && !isYouTubeWatch && !v?.trim()) {
          return "Reference metadata is required for non-tutorial missions";
        }
        return null;
      },
      targetUsername: (v, values) => {
        const isFollowOrJoin = [
          "follow_artist",
          "subscribe",
          "follow_account",
          "join_group",
          "join_channel",
        ].includes(values.actionType);
        if (isFollowOrJoin && !v?.trim()) {
          return "Account username is required for follow, subscribe, or join actions";
        }
        return null;
      },
      minRequiredLikes: (v, values) => {
        const requiresMinLikes = [
          "create_post_or_reel",
          "create_post_or_video",
        ].includes(values.actionType);
        if (requiresMinLikes && (!v || v < 1)) {
          return "Minimum likes must be at least 1 for this action";
        }
        return null;
      },
    },
  });

  useEffect(() => {
    if (form.values.title) {
      document.title = `Update Mission: ${form.values.title} | Virel Admin`;
    } else {
      document.title = "Update Mission | Virel Admin";
    }
  }, [form.values.title]);

  // Fetch Mission Data
  useEffect(() => {
    if (!taskId) return;
    const fetchMission = async () => {
      setIsLoading(true);
      const endpoint = Endpoints.GetMission(taskId);
      const r = await PerformRequest<any>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (r.status === HttpCodes.Success) {
        const task = r.data.data;
        setLoadedTask(task);
        form.setValues({
          title: task.title || "",
          description: task.description || "",
          type: task.type || "youtube",
          actionType: task.actionType || "default",
          youtubeUrl:
            task.youtubeUrl ||
            (task.type === "youtube" ? task.platformUrl : "") ||
            "",
          spotifyUrl:
            task.spotifyUrl ||
            (task.type === "spotify" ? task.platformUrl : "") ||
            "",
          platformUrl: task.platformUrl || "",
          requiredListens: task.requiredListens || 1,
          points: task.points || 50,
          currency: task.currency || "COIN",
          targetCompletions: task.targetCompletions || 1000,
          startDate: task.startDate ? new Date(task.startDate) : null,
          expiresAt: task.expiresAt ? new Date(task.expiresAt) : null,
          campaign: task.campaign
            ? typeof task.campaign === "object"
              ? task.campaign._id
              : (task.campaign as string)
            : null,
          order: task.order || 0,
          status: task.status || "draft",
          requiresProofPost: !!task.requiresProofPost,
          platforms: task.platforms || [],
          platformRule: task.platformRule || "any",
          minLevel: task.minLevel || 1,
          verificationInstruction: task.verificationInstruction || "",
          minWatchDurationSeconds: task.minWatchDurationSeconds || 40,
          watchToEnd: !!task.watchToEnd,
          cooldownSeconds: task.cooldownSeconds || 0,
          expireAtEndOfDay: false,
          isTutorial: !!task.isTutorial,
          squadsOnly: !!task.squadsOnly,
          thumbnailUrl: task.thumbnailUrl || "",
          sampleImages: task.sampleImages || [],
          proofInstructions: "",
          minRequiredLikes: task.minRequiredLikes || null,
          targetUsername: task.targetUsername || "",
          targetBrandName: "",
          targetPostText: task.targetPostText || "",
        });
      } else {
        setError("Failed to fetch activity details");
      }
      setIsLoading(false);
    };
    fetchMission();
  }, [taskId]);

  const handleActionTypeChange = (val: string | null) => {
    form.setFieldValue("actionType", val || "default");
    const type = form.values.type;

    if (type === "youtube" && val === "watch") {
      form.setFieldValue("verificationInstruction", "");
      return;
    }
    form.setFieldValue(
      "verificationInstruction",
      "Upload screenshot proof showing the action is completed.",
    );
  };

  const LabelWithInfo = ({
    label,
    description,
  }: {
    label: string;
    description: string;
  }) => {
    const [opened, { open, close }] = useDisclosure(false);

    return (
      <Group gap={6} align="center" wrap="nowrap">
        <span>{label}</span>
        <Popover
          width={320}
          position="bottom-start"
          withArrow
          shadow="md"
          opened={opened}
        >
          <Popover.Target>
            <span
              onMouseEnter={open}
              onMouseLeave={close}
              style={{
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
                lineHeight: 1,
              }}
              aria-label={`${label} info`}
            >
              <IconInfoCircleFilled
                size={14}
                color="var(--mantine-color-brand-6)"
              />
            </span>
          </Popover.Target>
          <Popover.Dropdown onMouseEnter={open} onMouseLeave={close}>
            <Text size="sm">{description}</Text>
          </Popover.Dropdown>
        </Popover>
      </Group>
    );
  };

  const labelWithInfo = (label: string, description: string) => (
    <LabelWithInfo label={label} description={description} />
  );

  // estimatedPayout was unused, removed it.

  // uploadSampleImages is now handled by the component directly

  const submit = async (status: TaskStatus) => {
    console.log(status);
    if (!taskId) return;

    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    // Upload pending sample images
    let finalSampleImages: (string | File)[] = [...form.values.sampleImages];
    const pendingFiles = finalSampleImages.filter(
      (img): img is File => img instanceof File,
    );

    if (pendingFiles.length > 0) {
      try {
        const uploadResults = await UploadFiles(pendingFiles);
        const uploadedUrls = uploadResults
          .map((res) => res?.data?.data?.url)
          .filter((url): url is string => typeof url === "string" && !!url);

        let urlIndex = 0;
        finalSampleImages = finalSampleImages
          .map((img) => {
            if (img instanceof File) {
              return uploadedUrls[urlIndex++] || "";
            }
            return img;
          })
          .filter((url) => !!url);
      } catch (err) {
        setIsSubmitting(false);
        notifications.show({
          title: "Upload failed",
          message: "Failed to upload sample images",
          color: "red",
        });
        return;
      }
    }

    const validation = form?.validate();
    if (validation.hasErrors) {
      setIsSubmitting(false);
      notifications.show({
        title: "Validation Error",
        message: "Please fix the form errors before proceeding.",
        color: "red",
      });
      // Scroll to first error field
      const firstErrorKey = Object.keys(validation.errors)[0];
      if (firstErrorKey) {
        const errorElement = document.querySelector(
          `[name="${firstErrorKey}"], #${firstErrorKey}, [id*="${firstErrorKey}"]`,
        );
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          (errorElement as HTMLElement).focus?.();
        }
      }
      return;
    }

    const endpoint = Endpoints.UpdateMission(taskId);

    // Calculate expiresAt based on expireAtEndOfDay flag
    let expiresAtValue: string | null = null;
    if (form?.values.expireAtEndOfDay) {
      // Set to 11:59 PM Nigerian time (WAT - UTC+1)
      const baseDate = form?.values.expiresAt || new Date();
      const endOfDay = new Date(baseDate);
      endOfDay.setHours(22, 59, 59, 999); // 22:59:59.999 UTC = 23:59:59.999 WAT (UTC+1)
      expiresAtValue = endOfDay.toISOString();
    } else if (form?.values.expiresAt) {
      const baseDate = form?.values.expiresAt;
      const endOfDay = new Date(baseDate);
      endOfDay.setHours(23, 59, 59, 999);
      expiresAtValue = endOfDay.toISOString();
    }

    const payload = {
      title: form?.values.title?.trim() || "",
      description: form?.values.description?.trim() || "",
      type: form?.values.type,
      actionType: form?.values.actionType,
      campaign: form?.values.isTutorial ? null : form?.values.campaign,
      order: Number(form?.values.order),
      youtubeUrl: form?.values.youtubeUrl?.trim() || "",
      spotifyUrl: form?.values.spotifyUrl?.trim() || "",
      platformUrl: form?.values.platformUrl?.trim() || "",
      requiredListens: Number(form?.values.requiredListens),
      status, // Update status based on button clicked
      points: Number(form?.values.points),
      currency: form?.values.currency || "COIN",
      targetCompletions: Number(form?.values.targetCompletions),
      startDate: form?.values.isTutorial
        ? null
        : form?.values.startDate
          ? form?.values.startDate.toISOString()
          : null,
      expiresAt: form?.values.isTutorial ? null : expiresAtValue,
      verificationInstruction: form?.values.verificationInstruction,
      requiresProofPost: form?.values.requiresProofPost,
      minWatchDurationSeconds: Number(form?.values.minWatchDurationSeconds),
      watchToEnd: form?.values.watchToEnd,
      cooldownSeconds: Number(form?.values.cooldownSeconds),
      platforms: form?.values.platforms,
      platformRule: form?.values.platformRule,
      isTutorial: form?.values.isTutorial,
      squadsOnly: form?.values.isTutorial ? false : form?.values.squadsOnly,
      thumbnailUrl: form.values.thumbnailUrl || "",
      sampleImages: finalSampleImages,
      proofInstructions: "",
      minRequiredLikes: form.values.minRequiredLikes,
      targetUsername: form.values.targetUsername?.trim() || "",
      targetBrandName: "",
      targetPostText: form.values.targetPostText?.trim() || "",
      // Only include minLevel for standalone missions (no campaign), not for tutorials
      ...(form?.values.isTutorial
        ? {}
        : form?.values.campaign
          ? {}
          : { minLevel: form?.values.minLevel }),
    };
    console.log("[Frontend] Final Payload sent to API:", payload);

    const r = await PerformRequest<UpdateMissionApiResponse>({
      method: endpoint.method,
      route: endpoint.route,
      data: payload,
      onError: () => setError("Failed to update activity"),
    });

    // Accept 200 or 201
    if (r.status >= 200 && r.status < 300) {
      setSuccess("Activity updated successfully");
      notifications.show({
        title: "Success",
        message: "Activity updated successfully",
        color: "green",
      });
      setIsSubmitting(false);
      allowNavigateRef.current = true;
      setTimeout(() => {
        navigate("/d/missions");
      }, 1000);
      return;
    }

    // Map API errors to form if they exist
    if ((r.data as any)?.errors) {
      form.setErrors((r.data as any).errors);
    }

    setError(r.message || r.data?.message || "Failed to update activity");
    notifications.show({
      title: "Error",
      message: r.message || r.data?.message || "Failed to update activity",
      color: "red",
    });
    setIsSubmitting(false);
  };

  const isMusic = ["youtube", "spotify"].includes(form?.values.type);

  if (isLoading) {
    return (
      <Center h={400}>
        <Loader color="brand" type="bars" />
      </Center>
    );
  }

  return (
    <div className="tasks-page">
      <Container fluid px="md">
        <div className="task-create-hero">
          <Group
            justify="space-between"
            align="flex-start"
            className="hero-row"
          >
            <div>
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                leftSection={<IconArrowLeft size={14} />}
                onClick={() => navigate("/d/missions")}
                className="hero-back"
              >
                Back to Activities
              </Button>
              <Group gap="xs" mt={10}>
                <Badge variant="light" color="brand">
                  Activities
                </Badge>
                <Badge
                  variant="light"
                  color={form?.values.status === "active" ? "green" : "gray"}
                >
                  {form?.values.status === "active" ? "Active" : "Draft"}
                </Badge>
              </Group>
              <Title order={1} className="hero-title" mt={10}>
                Update Activity
              </Title>
              <Text className="hero-subtitle" mt={6}>
                Modify the details of your activity.
              </Text>
            </div>

            <Group gap="sm">
              <Button
                variant="default"
                leftSection={<IconDeviceFloppy size={16} />}
                loading={isSubmitting}
                onClick={() => submit("draft")}
              >
                Save as Draft
              </Button>
              <Button
                className="cta-glow"
                leftSection={<IconRocket size={16} />}
                loading={isSubmitting}
                onClick={() => submit("active")}
              >
                {form?.values.status === "active" ? "Update" : "Publish"}
              </Button>
            </Group>
          </Group>
        </div>

        <Stack gap="lg" mt="lg">
          {(error || success) && (
            <Alert
              color={error ? "red" : "green"}
              variant="light"
              className="task-create-alert"
            >
              {error || success}
            </Alert>
          )}

          <SimpleGrid cols={{ base: 1, lg: 3 }} spacing="lg">
            <Paper
              withBorder
              radius="md"
              p="lg"
              className="card-glass"
              style={{ gridColumn: "span 2" }}
            >
              <Stack gap="md">
                <Group gap="xs">
                  <IconBolt size={18} />
                  <Text fw={700}>Activity Setup</Text>
                </Group>

                <Divider />

                <GridSection
                  label="Campaign & Type"
                  description="Associate this activity with a campaign or leave as generic. Tutorial activities cannot be part of a campaign."
                >
                  {!form?.values.isTutorial && (
                    <>
                      <Select
                        label="Campaign (Optional)"
                        placeholder="Select a campaign"
                        data={campaignOptions}
                        clearable
                        {...form?.getInputProps("campaign")}
                        mb="md"
                      />

                      {!form?.values.campaign && (
                        <Select
                          label="Minimum User Level"
                          description="Users must reach this level to access this activity (standalone only)."
                          data={levels
                            .sort((a, b) => a.level - b.level)
                            .map((l) => ({
                              value: String(l.level),
                              label: `Level ${l.level} - ${l.name} (${l.minCoins}+ coins)`,
                            }))}
                          value={String(form?.values.minLevel)}
                          onChange={(v) =>
                            form?.setFieldValue("minLevel", Number(v) || 1)
                          }
                          mb="md"
                        />
                      )}

                      {form?.values.campaign && (
                        <NumberInput
                          label="Order in Campaign"
                          description="Lower numbers appear first. 0 = Unordered/Start."
                          min={0}
                          mb="md"
                          {...form?.getInputProps("order")}
                        />
                      )}
                    </>
                  )}

                  <Switch
                    label={labelWithInfo(
                      "Is Tutorial Activity?",
                      "Mark this activity as a tutorial. It will only be shown to unmonetized users and will not award coins.",
                    )}
                    description="Hide from monetized users, award no coins"
                    mt="sm"
                    mb="sm"
                    {...form?.getInputProps("isTutorial", {
                      type: "checkbox",
                    })}
                    onChange={(e) => {
                      const checked = e.currentTarget.checked;
                      form?.setFieldValue("isTutorial", checked);
                      if (checked) {
                        form?.setFieldValue("campaign", null);
                        form?.setFieldValue("minLevel", 1);
                        form?.setFieldValue("startDate", null);
                        form?.setFieldValue("expiresAt", null);
                        form?.setFieldValue("expireAtEndOfDay", false);
                        form?.setFieldValue("squadsOnly", false);
                      }
                    }}
                  />

                  {!form?.values.isTutorial && (
                    <Switch
                      label={labelWithInfo(
                        "Squads Only",
                        "When enabled, this activity is exclusively accessible to users who are members or creators of a squad. Users without a squad cannot see or complete this mission.",
                      )}
                      description="Restrict access to squad members and creators only"
                      mt="sm"
                      mb="lg"
                      color="violet"
                      {...form?.getInputProps("squadsOnly", {
                        type: "checkbox",
                      })}
                    />
                  )}

                  <Switch
                    label={labelWithInfo(
                      "Requires a Link (video/post proof)",
                      "When ON, members submit a LINK (e.g. their Instagram video URL) as proof instead of a screenshot. Use for video/UGC opportunities like the Chowdeck video.",
                    )}
                    description="Members paste a video/post link instead of a screenshot"
                    mt="sm"
                    mb="lg"
                    color="violet"
                    {...form?.getInputProps("requiresProofPost", {
                      type: "checkbox",
                    })}
                  />

                  <Text size="sm" fw={500} mb="xs">
                    Opportunity Type
                  </Text>
                  <Select
                    data={[
                      { value: "youtube", label: "YouTube" },
                      { value: "twitter", label: "Twitter / X" },
                      { value: "spotify", label: "Spotify" },
                      { value: "facebook", label: "Facebook" },
                      { value: "instagram", label: "Instagram" },
                      { value: "whatsapp", label: "WhatsApp" },
                      { value: "telegram", label: "Telegram" },
                      { value: "tiktok", label: "TikTok" },
                      { value: "visit", label: "Visit Website" },
                      { value: "form", label: "Fill Form" },
                      { value: "share", label: "Share Content" },
                      { value: "other", label: "Other" },
                    ]}
                    {...form?.getInputProps("type")}
                    // Allow changing type for existing mission but warn? Usually fine.
                    onChange={(val) => {
                      form.setFieldValue("type", val as any);
                      // Reset actionType when type changes
                      const nextActions = ACTIONS_BY_TYPE[val || ""] || [];
                      handleActionTypeChange(
                        nextActions[0]?.value || "default",
                      );
                    }}
                  />

                  {ACTIONS_BY_TYPE[form.values.type]?.length > 0 && (
                    <Select
                      label="Action"
                      mt="md"
                      data={ACTIONS_BY_TYPE[form.values.type]}
                      value={form.values.actionType}
                      onChange={handleActionTypeChange}
                    />
                  )}

                  {form?.values.type === "share" && (
                    <GridSection
                      label="Share Settings"
                      description="Select platforms and completion requirements."
                    >
                      <Checkbox.Group
                        label="Required Platforms"
                        description="Select the platforms for this activity"
                        withAsterisk
                        mb="md"
                        {...form?.getInputProps("platforms")}
                      >
                        <Group mt="xs">
                          <Checkbox value="instagram" label="Instagram" />
                          <Checkbox value="whatsapp" label="Whatsapp" />
                          <Checkbox value="tiktok" label="TikTok" />
                        </Group>
                      </Checkbox.Group>

                      <Radio.Group
                        label="Completion Rule"
                        description="Does the user need to post on ALL selected platforms or just ONE?"
                        withAsterisk
                        mb="md"
                        {...form?.getInputProps("platformRule")}
                      >
                        <Group mt="xs">
                          <Radio value="any" label="Any (Choose One)" />
                          <Radio value="all" label="All (Must do all)" />
                        </Group>
                      </Radio.Group>
                    </GridSection>
                  )}

                  {["create_post_or_reel", "create_post_or_video"].includes(
                    form.values.actionType,
                  ) && (
                    <NumberInput
                      label="Minimum Required Likes"
                      min={1}
                      {...form.getInputProps("minRequiredLikes")}
                    />
                  )}
                </GridSection>

                {form?.values.type === "youtube" &&
                  form?.values.actionType === "watch" && (
                    <GridSection
                      label="Video Settings"
                      description="Configure video duration and completion rules."
                    >
                      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                        <NumberInput
                          label="Min Watch Duration (Seconds)"
                          description="How long user must watch (default 40s)"
                          min={1}
                          {...form?.getInputProps("minWatchDurationSeconds")}
                          disabled={form?.values.watchToEnd}
                        />
                        <NumberInput
                          label="Cooldown (Seconds)"
                          description="Wait time before repeating (0 = no wait)"
                          min={0}
                          {...form?.getInputProps("cooldownSeconds")}
                        />
                      </SimpleGrid>
                      <Switch
                        label="Require Watch to End"
                        description="If enabled, overrides timer and waits for video end."
                        mt="md"
                        {...form?.getInputProps("watchToEnd", {
                          type: "checkbox",
                        })}
                      />
                    </GridSection>
                  )}

                {/* Custom Thumbnail section removed */}

                <TextInput
                  label={labelWithInfo(
                    "Title",
                    "The activity name users will see in the app. Keep it short and clear.",
                  )}
                  placeholder="e.g. Sign up for Opay"
                  {...form?.getInputProps("title")}
                />
                <Textarea
                  label={labelWithInfo(
                    "Description",
                    "Explain what users need to do to complete this activity.",
                  )}
                  placeholder="Detailed instructions..."
                  minRows={3}
                  autosize
                  {...form?.getInputProps("description")}
                />

                <Stack gap="sm">
                  <Text size="sm" fw={500}>
                    Sample Images (Required)
                  </Text>
                  <Text size="xs" c="dimmed">
                    Upload one or more sample proof images. These are shown to
                    users and used by AI to validate submitted screenshots.
                  </Text>
                  <SampleImageUploader
                    value={form.values.sampleImages}
                    onChange={(urls) => {
                      form.setFieldValue("sampleImages", urls);
                      form.clearFieldError("sampleImages");
                    }}
                    error={form.errors.sampleImages as string}
                  />
                </Stack>

                {[
                  "follow_artist",
                  "subscribe",
                  "follow_account",
                  "join_group",
                  "join_channel",
                ].includes(form.values.actionType) && (
                  <TextInput
                    label="Account Username / Handle"
                    placeholder="e.g. virel_app (the target username to follow/subscribe to)"
                    description="Enter the exact username of the target account (without '@'). The AI will verify this on the user's screenshot."
                    withAsterisk
                    {...form.getInputProps("targetUsername")}
                    onBlur={(e) => {
                      const sanitized = sanitizeUsername(e.currentTarget.value);
                      form.setFieldValue("targetUsername", sanitized);
                    }}
                  />
                )}

                {!form.values.isTutorial && !(form.values.type === "youtube" && form.values.actionType === "watch") && (
                  <Textarea
                    label="Reference Metadata / Caption"
                    placeholder="Paste the caption, post text, or other key reference details here. The AI will verify this metadata on the user's screenshot."
                    description="Required for non-tutorial missions."
                    minRows={4}
                    autosize
                    withAsterisk
                    {...form.getInputProps("targetPostText")}
                  />
                )}

                {form?.values.type === "youtube" && (
                  <TextInput
                    label={
                      form.values.actionType === "subscribe"
                        ? "Channel Link"
                        : form.values.actionType === "comment"
                          ? "Video Link"
                          : "YouTube Video URL"
                    }
                    placeholder="https://youtube.com/..."
                    {...form?.getInputProps("youtubeUrl")}
                    rightSection={<Image src={youtubeLogo} w={20} />}
                  />
                )}

                {form?.values.type === "spotify" && (
                  <TextInput
                    label={
                      form.values.actionType === "save_to_playlist"
                        ? "Song Link"
                        : form.values.actionType === "follow_artist"
                          ? "Artist Profile Link"
                          : "Spotify Track URL"
                    }
                    placeholder="https://open.spotify.com/..."
                    {...form?.getInputProps("spotifyUrl")}
                    rightSection={<Image src={spotifyLogo} w={20} />}
                  />
                )}

                {[
                  "twitter",
                  "facebook",
                  "instagram",
                  "whatsapp",
                  "telegram",
                  "tiktok",
                ].includes(form?.values.type) && (
                  <TextInput
                    label="Platform Link"
                    placeholder="https://..."
                    {...form?.getInputProps("platformUrl")}
                  />
                )}

                {!isMusic && (
                  <TextInput
                    label="Action URL (Optional)"
                    placeholder="https://..."
                    {...form?.getInputProps("platformUrl")}
                    description="The link the user will be directed to."
                  />
                )}

                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  {isMusic &&
                    ![
                      "subscribe",
                      "comment",
                      "save_to_playlist",
                      "follow_artist",
                    ].includes(form?.values.actionType) && (
                      <NumberInput
                        label={labelWithInfo(
                          "Required Listens",
                          "How many times users must listen to the track.",
                        )}
                        min={1}
                        {...form?.getInputProps("requiredListens")}
                      />
                    )}
                  <Select
                    label="Currency"
                    data={[{ value: "COIN", label: "Coins" }]}
                    value={form?.values.currency}
                    onChange={(value) => {
                      if (value) form?.setFieldValue("currency", value);
                    }}
                  />
                </SimpleGrid>

                <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
                  <NumberInput
                    label="Reward (₦)"
                    min={1}
                    {...form?.getInputProps("points")}
                  />
                  <NumberInput
                    label="Target completions"
                    min={1}
                    {...form?.getInputProps("targetCompletions")}
                  />
                </SimpleGrid>

                {!form?.values.isTutorial && (
                  <>
                    <DateInput
                      label={labelWithInfo(
                        "Start Date (Optional)",
                        "Select the date when this activity should begin. Defaults to immediately if blank.",
                      )}
                      placeholder="Pick date"
                      minDate={getMinStartDate()}
                      value={form.values.startDate}
                      onChange={(v) => {
                        const next =
                          v === null
                            ? null
                            : typeof v === "string"
                              ? new Date(v)
                              : v;
                        form.setFieldValue("startDate", next);
                      }}
                      clearable
                      mt="md"
                      itemID="startDate"
                    />

                    <Switch
                      label={labelWithInfo(
                        "Expire at end of today",
                        "When enabled, the activity will automatically expire at 11:59 PM Nigerian time (WAT) today.",
                      )}
                      description="Activity expires at 11:59 PM WAT (Today)"
                      mt="sm"
                      {...form?.getInputProps("expireAtEndOfDay", {
                        type: "checkbox",
                      })}
                    />

                    {!form?.values.expireAtEndOfDay && (
                      <DateInput
                        label={labelWithInfo(
                          "Expiry Date",
                          "Select the date when this activity should expire.",
                        )}
                        placeholder="Pick date"
                        minDate={getMinExpiresAt()}
                        value={form.values.expiresAt}
                        onChange={(v) => {
                          const next =
                            v === null
                              ? null
                              : typeof v === "string"
                                ? new Date(v)
                                : v;
                          form.setFieldValue("expiresAt", next);
                        }}
                        clearable
                        mt="md"
                        itemID="expiresAt"
                      />
                    )}
                  </>
                )}
              </Stack>
            </Paper>

            <Paper withBorder radius="md" p="lg" className="card-glow">
              <Stack gap="md">
                <Group justify="space-between">
                  <Text fw={700}>Preview</Text>
                  <Badge color="brand" variant="light">
                    Preview
                  </Badge>
                </Group>
                <div className="preview-card">
                  <Text fw={700} className="preview-title">
                    {form?.values?.title || "Activity Title"}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {form?.values?.type?.toUpperCase()}
                  </Text>
                  <Text size="sm" mt={4}>
                    Reward: {form?.values?.points} Coins
                  </Text>
                </div>
              </Stack>
            </Paper>
          </SimpleGrid>

          {taskId && !form.values.isTutorial && (
            <AccessTierControl
              kind="mission"
              id={taskId}
              currentTier={loadedTask?.accessTier}
              currentAllowUsers={loadedTask?.allowUsers || []}
              currentBlockUsers={loadedTask?.blockUsers || []}
            />
          )}
        </Stack>
      </Container>

      <Modal
        opened={blocker.state === "blocked"}
        onClose={() => blocker.reset?.()}
        title="Unsaved Changes"
        centered
        radius="md"
      >
        <Stack gap="md">
          <Text size="sm">
            You have unsaved changes. Are you sure you want to leave this page? Your changes will be lost.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => blocker.reset?.()}>
              Stay on Page
            </Button>
            <Button color="red" onClick={() => blocker.proceed?.()}>
              Leave Page
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
};

const GridSection = ({ label, description, children }: any) => (
  <div style={{ marginBottom: 16 }}>
    <Text fw={600}>{label}</Text>
    {description && (
      <Text size="xs" c="dimmed" mb="xs">
        {description}
      </Text>
    )}
    {children}
  </div>
);

export default MissionUpdate;
